// RBAC helpers for CustomersPage.
// Ported faithfully from frontend-bk/erp/src/data/auth.js canSeeAllCustomers.
//
// canSeeAllCustomers source (auth.js lines 753–760):
//   admin → true
//   director → true
//   manager → true
//   coordinator → ĐÃ KHOÁ 23/08/2026 (quyết định 0050), xem chú thích dưới
//   accounting → true
//   cskh department → true (CSKH sees full list for delivery mgmt)
//   sale → true (sales need customer list context for complaint intake)
//   reception department → true (QĐ 0356, CEO chốt 12/10/2026)
//   everyone else → false

import { ROLE } from "@/lib/auth/roles";
import { ACTION } from "@/lib/auth/actions";
import { deptWriteDoorClosed } from "@/lib/auth/dept-door";
import { hasPermission } from "@/lib/authv2/permissions";
import { useAuthV2 } from "@/lib/authv2/store";
import { TECHNICAL_BOARD_DEPARTMENTS } from "@/features/medals/permissions";

export type CurrentUser = {
  id: string;
  role: string;
  department: string;
  departments?: string[]; // nhiều phòng ban (hồ sơ cũ của vai Điều phối)
};

/**
 * Exact port from auth.js canSeeAllCustomers.
 * Sales roles/departments → true for complaint intake and order follow-up.
 * CSKH staff → true (needs full list for delivery management).
 *
 * NOTE: Department-driven gate (CSKH dept sees all customers for delivery
 * mgmt). No clean BE action mapping during Model A migration — kept on
 * role/dept check. Revisit if BE adds dept-aware policy.
 */
export function canSeeAllCustomers(user: CurrentUser | null): boolean {
  if (!user) return false;
  if (user.role === ROLE.Admin || user.role === ROLE.Director) return true;
  // Vai Điều phối bị khoá 23/08/2026 (quyết định 0050) nên KHÔNG còn ở đây.
  // Đây là cổng THẬT của trang Khách hàng, không phải trang trí: layout
  // `_app.tsx` chỉ chặn người chưa đăng nhập, còn `GET /customers` phía máy
  // chủ không gắn `auth.Allow` nào và đọc bằng AllBranchScope(). Cấp lại vai
  // ở đây là mở thẳng danh sách khách toàn chuỗi cho vai 0 quyền.
  // Trưởng phòng chụp, trang điểm, hậu kỳ (hoặc chưa gán phòng) chỉ việc phòng
  // mình — không danh bạ khách (CEO chốt Q03 17/10/2026); máy chủ cắt crm.read.
  if (user.role === ROLE.Manager) {
    const dept = (user.department || "").trim().toLowerCase();
    return dept !== "" && !(TECHNICAL_BOARD_DEPARTMENTS as readonly string[]).includes(dept);
  }
  // Vai chuỗi (decision 0036): trưởng cửa hàng + quản lý cụm thấy danh sách
  // khách — BE cắt theo branch scope nên "all" thực tế là phạm vi được gán.
  if (user.role === ROLE.BranchManager || user.role === ROLE.AreaManager) return true;
  if (user.role === ROLE.Accounting) return true;
  if (user.role === ROLE.Sale || user.department === "sale" || user.department === "sales") return true;
  if (user.department === "cskh") return true;
  // Lễ tân xem trọn danh bạ từ 12/10/2026 (QĐ 0356 thay tầng "chỉ tra một số"
  // của QĐ 0089). Cổng máy chủ: phòng "reception" trong readDepts của
  // backend/internal/core/customer/handler.go.
  if (user.department === "reception") return true;
  return false;
}

/**
 * Người đang đăng nhập có được bấm "+ Thêm" khách mới không — bản sao cổng
 * `POST /customers` (`allowCreateCustomer` ở backend/internal/core/customer/
 * handler.go): quyền `customer.write`, HOẶC phòng CSKH / lễ tân với vai được
 * ghi qua cửa phòng ban, HOẶC QC phòng lễ tân (CEO mở riêng cửa này cho họ
 * 12/10/2026, QĐ 0356).
 *
 * Tách khỏi `canWriteCustomer`: lễ tân thêm được khách nhưng KHÔNG sửa hồ sơ
 * khách đã có — nút "Chỉnh sửa thông tin" vẫn đi theo `customer.write`.
 */
export function canCreateCustomer(user: CurrentUser | null): boolean {
  if (!user) return false;
  if (hasPermission(ACTION.CustomerWrite)) return true;
  const role = user.role.trim().toLowerCase();
  const dept = user.department.trim().toLowerCase();
  if (role === ROLE.QC) return dept === "reception";
  return (dept === "cskh" || dept === "reception") && !deptWriteDoorClosed(role);
}

/**
 * Người đang đăng nhập mở được hồ sơ khách theo mã không — GET /customers/:id
 * (trang hồ sơ khách đọc đúng cửa này) chỉ mở cho người xem được sổ khách, cổng
 * máy chủ QĐ 0089 = canSeeAllCustomers. Đơn tạo từ 17/09/2026 đều gắn hồ sơ khách
 * (HRS-135 B4): trang đơn hỏi bừa thì lễ tân / thợ / hậu kỳ nhận 403, còn nút
 * "Mở hồ sơ" dẫn họ tới trang "Không tìm thấy khách hàng".
 */
export function useCanOpenCustomerProfile(): boolean {
  const me = useAuthV2((s) => s.currentStaff);
  return canSeeAllCustomers(me ? { id: me.id, role: me.role, department: me.department } : null);
}

/**
 * Can user create/update/delete customers?
 * Cổng thật là quyền `customer.write` của máy chủ (admin, director, manager,
 * branch_manager — KHÔNG có coordinator từ 23/08/2026, quyết định 0050).
 */
export function canWriteCustomer(user: CurrentUser | null): boolean {
  if (!user) return false;
  return hasPermission(ACTION.CustomerWrite);
}

/**
 * Can user trigger sync-center for a customer?
 * Same as write — BE guards the same endpoint with writeRoles.
 */
export function canSyncCenter(user: CurrentUser | null): boolean {
  if (!user) return false;
  return hasPermission(ACTION.CustomerWrite);
}
